import {useLayoutEffect, useRef, useState} from 'react';
import {Box, Button, Flex, Heading, HStack, Spinner, Text, VStack} from '@chakra-ui/react';
import {
    clippingColor,
    clippingOf,
    Histogram,
    HISTOGRAM_BINS,
    HistogramChannel,
    histogramPeak,
    HistogramSeries,
    seriesOf,
    smoothBins,
} from '../../domain/histogram';
import {HistogramSource, useHistogram} from './useHistogram';

const HEIGHT_PX = 110;
const STORAGE_KEY = 'spot-drive-viewer-histogram-channel';

const CHANNELS: { value: HistogramChannel; label: string; title: string }[] = [
    {value: 'rgb', label: 'RGB', title: 'Színcsatornák együtt'},
    {value: 'red', label: 'R', title: 'Vörös csatorna'},
    {value: 'green', label: 'G', title: 'Zöld csatorna'},
    {value: 'blue', label: 'B', title: 'Kék csatorna'},
    {value: 'luminance', label: 'L', title: 'Fényerő'},
];

// Together the three add up to a light grey where they overlap, and to yellow, cyan or magenta
// where two do, as in Lightroom.
const RGB_FILL: Record<HistogramSeries, string> = {
    red: 'rgb(170, 35, 35)',
    green: 'rgb(35, 150, 35)',
    blue: 'rgb(35, 55, 175)',
    luminance: 'rgb(190, 190, 190)',
};
const SINGLE_FILL: Record<HistogramSeries, string> = {
    red: 'rgb(220, 70, 70)',
    green: 'rgb(70, 190, 70)',
    blue: 'rgb(80, 120, 230)',
    luminance: 'rgb(190, 190, 190)',
};

function loadChannel(): HistogramChannel {
    try {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        return CHANNELS.find((c) => c.value === stored)?.value ?? 'rgb';
    } catch {
        return 'rgb';
    }
}

function saveChannel(channel: HistogramChannel) {
    try {
        sessionStorage.setItem(STORAGE_KEY, channel);
    } catch {
        // Not remembered then.
    }
}

function drawHistogram(canvas: HTMLCanvasElement, histogram: Histogram, channel: HistogramChannel) {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, width, height);

    // Quarter lines, as Lightroom's.
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
    ctx.lineWidth = 1;
    for (const q of [0.25, 0.5, 0.75]) {
        const x = Math.round(width * q) + 0.5;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
    }

    const series = seriesOf(channel);
    const values = series.map((s) => smoothBins(histogram[s]));
    const peak = histogramPeak(values);
    const fills = channel === 'rgb' ? RGB_FILL : SINGLE_FILL;
    // Overlapping channels add up, as light does.
    ctx.globalCompositeOperation = channel === 'rgb' ? 'lighter' : 'source-over';
    series.forEach((s, n) => {
        ctx.beginPath();
        ctx.moveTo(0, height);
        values[n].forEach((value, i) => {
            ctx.lineTo(i / (HISTOGRAM_BINS - 1) * width, height - Math.min(value / peak, 1) * (height - 2));
        });
        ctx.lineTo(width, height);
        ctx.closePath();
        ctx.fillStyle = fills[s];
        ctx.fill();
    });
    ctx.globalCompositeOperation = 'source-over';
}

function formatShare(share: number): string {
    return `${(share * 100).toLocaleString('hu', {maximumFractionDigits: share < 0.01 ? 2 : 1})}%`;
}

/**
 * Corner triangle to show clipping: lit white for real clipping (all three channels at the end),
 * in a single colour channel's view in that channel's colour; dark otherwise.
 */
function ClippingIndicator({histogram, channel, end}: {
    histogram: Histogram;
    channel: HistogramChannel;
    end: 'shadows' | 'highlights';
}) {
    const {clipped, share} = clippingOf(histogram, channel, end);
    const what = end === 'shadows' ? 'Bebukott árnyékok' : 'Kiégett csúcsfények';
    // A single colour channel's view names it: its own clipping, not the picture's.
    const colorChannel = channel === 'rgb' || channel === 'luminance' ? undefined : CHANNELS.find((c) => c.value === channel)?.label;
    const title = clipped
        ? `${what}${colorChannel ? `: ${colorChannel}` : ''} (${formatShare(share)})`
        : `Nincsenek ${what.toLowerCase()}`;
    return <Box
        role="img"
        aria-label={title}
        title={title}
        data-clipped={clipped || undefined}
        position="absolute"
        top="4px"
        {...(end === 'shadows' ? {left: '4px'} : {right: '4px'})}
        boxSize="9px"
        // Pointing into its corner.
        clipPath={end === 'shadows' ? 'polygon(0 0, 100% 0, 0 100%)' : 'polygon(0 0, 100% 0, 100% 100%)'}
        bg={clipped ? clippingColor(channel) : 'whiteAlpha.300'}
    />;
}

interface HistogramViewProps {
    /** The shown image to read; null until it is decoded. */
    source: HistogramSource | null;
    /** The viewer has no picture to show (it can't be displayed, or failed to load). */
    unavailable?: boolean;
}

/** The photo's histogram, by colour channel or luminance, with clipping indicators, like Lightroom's. */
export function HistogramView({source, unavailable}: HistogramViewProps) {
    const {histogram, failed} = useHistogram(source);
    const [channel, setChannel] = useState(loadChannel);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useLayoutEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !histogram) return;
        drawHistogram(canvas, histogram, channel);
        // Redrawn at the new size, e.g. on the phones' sheet when the screen turns.
        const observer = new ResizeObserver(() => drawHistogram(canvas, histogram, channel));
        observer.observe(canvas);
        return () => observer.disconnect();
    }, [histogram, channel]);

    function select(value: HistogramChannel) {
        setChannel(value);
        saveChannel(value);
    }

    return <Box px={4} pt={4} bg="gray.900" color="white">
        <Flex align="center" justify="space-between" mb={2}>
            <Heading size="sm">Hisztogram</Heading>
            <HStack gap={0.5} role="group" aria-label="Csatorna">
                {CHANNELS.map((c) => <Button
                    key={c.value}
                    size="2xs"
                    minW={c.value === 'rgb' ? 9 : 6}
                    px={1}
                    variant="ghost"
                    color={channel === c.value ? 'white' : 'gray.400'}
                    bg={channel === c.value ? 'whiteAlpha.300' : undefined}
                    _hover={{bg: 'whiteAlpha.200'}}
                    aria-pressed={channel === c.value}
                    title={c.title}
                    onClick={() => select(c.value)}
                >
                    {c.label}
                </Button>)}
            </HStack>
        </Flex>
        <Box position="relative" h={`${HEIGHT_PX}px`} bg="blackAlpha.500" borderRadius="sm" overflow="hidden">
            {histogram && <>
                <canvas ref={canvasRef} style={{display: 'block', width: '100%', height: '100%'}}/>
                <ClippingIndicator histogram={histogram} channel={channel} end="shadows"/>
                <ClippingIndicator histogram={histogram} channel={channel} end="highlights"/>
            </>}
            {!histogram && !failed && !unavailable &&
                <Flex position="absolute" inset={0} align="center" justify="center"><Spinner size="sm" color="gray.400"/></Flex>
            }
            {!histogram && (failed || unavailable) &&
                <VStack position="absolute" inset={0} align="center" justify="center" gap={0}
                        fontSize="sm" color="gray.400">
                    <Text>A hisztogram nem érhető el.</Text>
                    <Text>Kapcsold be az "Eredeti" opciót!</Text>
                </VStack>
            }
        </Box>
    </Box>;
}
